import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, FolderInput, Workflow, Users, Headphones, Gavel, ShieldCheck, Plug, ScrollText, UserCircle2, ChevronDown, ChevronRight, Briefcase, BarChart3, Bot, PhoneCall, Mic, ClipboardCheck, Sparkles, FlaskConical, Send, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Nav groups are organised by user-task, not by module name:
 *   1. Daily work        — what the agent / supervisor does every hour
 *   2. Strategy & data   — portfolio + assignment decisioning
 *   3. Governance        — compliance, legal, audit
 *   4. Platform          — integrations, debtor portal
 *
 * Each group can be collapsed independently; "Daily work" is open by default
 * because that's the most-used cluster for every persona.
 */
const GROUPS = [
  {
    id: 'daily', label: 'Daily work', defaultOpen: true,
    items: [
      { to: '/app/command-center', label: 'Command Center', icon: Home, personas: ['manager', 'supervisor', 'admin', 'legal', 'compliance'] },
      { to: '/app/agent',          label: 'Agent Workbench', icon: Headphones, personas: ['agent', 'supervisor', 'manager'] },
      { to: '/app/supervisor',     label: 'Supervisor Cockpit', icon: Users,   personas: ['supervisor', 'manager'] },
    ],
  },
  {
    id: 'strategy', label: 'Strategy & data', defaultOpen: true,
    items: [
      { to: '/app/portfolios',     label: 'Portfolios',         icon: FolderInput, personas: ['manager', 'supervisor', 'admin'] },
      { to: '/app/assignments',    label: 'Decisioning Studio', icon: Workflow,    personas: ['manager', 'supervisor', 'admin'] },
    ],
  },
  {
    id: 'voice', label: 'AI Voice Agents', defaultOpen: true, accent: true,
    items: [
      { to: '/app/voice-agents',              label: 'Voice Dashboard',     icon: Bot,           personas: ['manager', 'supervisor', 'admin', 'compliance', 'agent', 'legal'] },
      { to: '/app/voice-agents/library',      label: 'Agent Library',       icon: Sparkles,      personas: ['manager', 'supervisor', 'admin', 'compliance'] },
      { to: '/app/voice-agents/create',       label: 'Create Agent',        icon: Mic,           personas: ['manager', 'admin'] },
      { to: '/app/voice-agents/designer',     label: 'Conversation Designer', icon: Workflow,    personas: ['manager', 'admin'] },
      { to: '/app/voice-agents/campaigns',    label: 'Campaigns',           icon: Send,          personas: ['manager', 'supervisor', 'admin'] },
      { to: '/app/voice-agents/simulator',    label: 'Test Simulator',      icon: FlaskConical,  personas: ['manager', 'supervisor', 'admin', 'compliance', 'agent'] },
      { to: '/app/voice-agents/review',       label: 'Call Review',         icon: ClipboardCheck, personas: ['supervisor', 'manager', 'compliance', 'legal'] },
      { to: '/app/voice-agents/analytics',    label: 'Analytics',           icon: BarChart3,     personas: ['manager', 'supervisor', 'admin'] },
      { to: '/app/voice-agents/compliance',   label: 'AI Compliance',       icon: ShieldCheck,   personas: ['compliance', 'manager', 'admin'] },
      { to: '/app/voice-agents/voices',       label: 'Voices & Scripts',    icon: BookOpen,      personas: ['manager', 'admin', 'compliance'] },
      { to: '/app/voice-agents/integrations', label: 'Voice Integrations',  icon: Plug,          personas: ['admin', 'manager'] },
    ],
  },
  {
    id: 'governance', label: 'Governance', defaultOpen: false,
    items: [
      { to: '/app/legal',          label: 'Legal Workbench', icon: Gavel,       personas: ['legal', 'manager', 'supervisor'] },
      { to: '/app/compliance',     label: 'Compliance & QA', icon: ShieldCheck, personas: ['compliance', 'manager', 'admin'] },
      { to: '/app/audit',          label: 'Audit Logs',      icon: ScrollText,  personas: ['admin', 'compliance', 'manager'] },
    ],
  },
  {
    id: 'platform', label: 'Platform', defaultOpen: false,
    items: [
      { to: '/app/integrations',   label: 'Integration Hub',   icon: Plug,        personas: ['admin', 'manager'] },
      { to: '/portal',             label: 'Debtor Portal (demo)', icon: UserCircle2, personas: ['agent', 'supervisor', 'manager', 'admin', 'compliance', 'legal'] },
    ],
  },
];

export const Sidebar = ({ persona }) => {
  const { sidebarCollapsed } = useApp();
  const [openGroups, setOpenGroups] = useState(
    Object.fromEntries(GROUPS.map((g) => [g.id, g.defaultOpen]))
  );

  const toggle = (id) => setOpenGroups((o) => ({ ...o, [id]: !o[id] }));

  // Filter items per persona but keep all groups for layout consistency.
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => it.personas.includes(persona.id)),
    extras: g.items.filter((it) => !it.personas.includes(persona.id)),
  }));

  return (
    <TooltipProvider delayDuration={120}>
      <aside
        className={cn(
          'hidden md:flex shrink-0 border-r border-border bg-gradient-ivory flex-col transition-[width] duration-300',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
        data-testid="sidebar"
      >
        <div className={cn('flex-1 overflow-y-auto scrollbar-thin', sidebarCollapsed ? 'px-1.5 py-3' : 'px-3 py-4')}>
          {!sidebarCollapsed && (
            <p className="px-3 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">For {persona.short}</p>
          )}

          {groups.map((g, idx) => {
            const hasItems = g.items.length > 0;
            if (!hasItems && g.extras.length === 0) return null;
            return (
              <div key={g.id} className={cn(idx > 0 && (sidebarCollapsed ? 'mt-3' : 'mt-4'))}>
                {!sidebarCollapsed && (
                  <button
                    onClick={() => toggle(g.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 mb-1 text-[10px] uppercase tracking-widest hover:text-foreground',
                      g.accent ? 'text-primary' : 'text-muted-foreground'
                    )}
                    data-testid={`sidebar-group-${g.id}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {g.accent && <Sparkles className="h-3 w-3" aria-hidden="true" />}
                      {g.label}
                    </span>
                    {openGroups[g.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                )}
                {sidebarCollapsed && idx > 0 && <div className="my-2 mx-auto w-6 h-px bg-border" />}
                {(openGroups[g.id] || sidebarCollapsed) && (
                  <nav className="space-y-0.5">
                    {g.items.map((n) => (
                      <NavItem key={n.to} {...n} collapsed={sidebarCollapsed} />
                    ))}
                    {!sidebarCollapsed && g.extras.length > 0 && g.items.length > 0 && (
                      <div className="my-1.5 h-px bg-border/60" />
                    )}
                    {g.extras.map((n) => (
                      <NavItem key={n.to} {...n} collapsed={sidebarCollapsed} muted />
                    ))}
                  </nav>
                )}
              </div>
            );
          })}
        </div>

        {!sidebarCollapsed && (
          <div className="px-4 py-3 border-t border-border bg-card/60">
            <div className="surface-card p-3 bg-gradient-to-br from-onyx to-onyx/90 text-background">
              <p className="text-[10px] uppercase tracking-widest text-primary/90 mb-1">SAMA-aligned</p>
              <p className="text-xs leading-relaxed text-background/80">All flows operate under contact window 09:00–21:00, opt-out enforcement & PDPL masking.</p>
            </div>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="px-2 py-3 border-t border-border bg-card/60 flex items-center justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="h-9 w-9 rounded-md bg-gradient-to-br from-onyx to-onyx/90 text-primary flex items-center justify-center text-[10px] font-semibold border border-onyx">SA</div>
              </TooltipTrigger>
              <TooltipContent side="right">SAMA-aligned • PDPL masking</TooltipContent>
            </Tooltip>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
};

const NavItem = ({ to, label, icon: Icon, collapsed, muted }) => {
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={to}
            className={({ isActive }) =>
              cn(
                'group flex items-center justify-center h-10 w-10 mx-auto rounded-lg transition-colors',
                isActive ? 'bg-primary/10 ring-1 ring-primary/30 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                muted && 'opacity-60'
              )
            }
          >
            <Icon className="h-4 w-4" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
          isActive
            ? 'bg-primary/10 text-foreground ring-1 ring-primary/30'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          muted && 'opacity-65'
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('h-4 w-4', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
          <span className="truncate">{label}</span>
          {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
        </>
      )}
    </NavLink>
  );
};

export default Sidebar;
